import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  loadPaymentWidget,
  PaymentWidgetInstance,
} from '@tosspayments/payment-widget-sdk'
import { useQuery } from '@tanstack/react-query'
import { v4 as uuidv4 } from 'uuid'

export const Payment: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const params = new URLSearchParams(location.search)
  const amount = params.get('amount')
  const customerName = params.get('customerName') || ''
  const email = params.get('email') || ''

  const [totalPrice, setTotalPrice] = useState(0)
  const [orderId, setOrderId] = useState('')
  const clientKey = process.env.REACT_APP_KEY ?? ''

  const paymentWidgetRef = useRef<PaymentWidgetInstance | null>(null)
  const paymentMethodsWidgetRef = useRef<ReturnType<
    PaymentWidgetInstance['renderPaymentMethods']
  > | null>(null)

  function usePaymentWidget(clientKey: string, customerKey: string) {
    return useQuery({
      queryKey: ['payment-widget', clientKey, customerKey],
      queryFn: () => {
        return loadPaymentWidget(clientKey, customerKey)
      },
    })
  }

  const { data: paymentWidget } = usePaymentWidget(clientKey, orderId)

  const generateOrderId = () => {
    return `anonymous_${uuidv4()}`
  }

  const handlePayment = () => {
    const paymentWidget = paymentWidgetRef.current

    if (!paymentWidget || !orderId || !amount) {
      alert('결제 정보가 올바르지 않습니다.')
      return
    }

    try {
      paymentWidget.requestPayment({
        orderId,
        orderName: `${Number(amount).toLocaleString()}원 결제 `,
        customerName: customerName || '익명',
        customerEmail: email || '',
        successUrl: `${window.location.origin}/anonymous/payment-success?email=${email}`,
        failUrl: `${window.location.origin}/anonymous/payment-fail`,
      })
    } catch (err) {
      console.error('Payment error:', err)
      alert('결제 중 오류가 발생했습니다.')
    }
  }

  useEffect(() => {
    if (!amount || isNaN(Number(amount))) {
      alert('올바른 금액이 필요합니다.')
      navigate('/')
      return
    }

    const newOrderId = generateOrderId()
    setOrderId(newOrderId)
    setTotalPrice(Number(amount))
  }, [amount, navigate])

  useEffect(() => {
    ;(async () => {
      if (orderId && totalPrice > 0) {
        const paymentWidget = await loadPaymentWidget(clientKey, orderId)
        const paymentMethodsWidget = paymentWidget.renderPaymentMethods(
          '#payment-widget',
          totalPrice,
          { variantKey: 'DEFAULT' },
        )

        paymentWidgetRef.current = paymentWidget
        paymentMethodsWidgetRef.current = paymentMethodsWidget
      }
    })()
  }, [orderId, totalPrice])

  useEffect(() => {
    const paymentMethodsWidget = paymentMethodsWidgetRef.current

    if (paymentMethodsWidget && totalPrice > 0) {
      paymentMethodsWidget.updateAmount(
        totalPrice,
        paymentMethodsWidget.UPDATE_REASON.COUPON,
      )
    }
  }, [totalPrice])

  if (!amount || isNaN(Number(amount))) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-xl font-bold">올바르지 않은 금액입니다.</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          >
            홈으로 돌아가기
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-md p-6">
      <div className="mb-6 text-center">
        <h1 className="mb-4 text-2xl font-bold">{email || '익명'} 결제</h1>
        <div className="rounded-lg bg-gray-100 p-4">
          <p className="text-lg font-semibold">결제 금액</p>
          <p className="text-2xl font-bold text-blue-600">
            {Number(amount).toLocaleString()}원
          </p>
        </div>
      </div>

      <div className="mb-6">
        <div id="payment-widget" />
      </div>

      <button
        onClick={handlePayment}
        className="w-full rounded-lg bg-blue-600 py-4 text-lg font-bold text-white hover:bg-blue-700 disabled:opacity-50"
        disabled={!orderId || totalPrice <= 0}
      >
        {totalPrice.toLocaleString()}원 결제하기
      </button>
    </div>
  )
}
